import { useState, useCallback } from 'react';
import {
  CreateCertificateRequest,
  CertificateResponse,
  VerificationResponse,
  certificateApi,
} from '../services/certificateApi';

export const useCertificateApi = () => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCertificate = useCallback(
    async (
      request: CreateCertificateRequest
    ): Promise<CertificateResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await certificateApi.createCertificate(request);

        if (!result.success) {
          setError(result.error || 'Failed to create certificate');
          return null;
        }

        return result.data || null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const deleteCertificate = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await certificateApi.deleteCertificate(id);

        if (!result.success) {
          setError(result.error || 'Failed to delete certificate');
          return false;
        }

        return true;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const verifyCertificate = useCallback(
    async (
      certificateNumber: string
    ): Promise<VerificationResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await certificateApi.verifyCertificate(certificateNumber);

        if (!result.success) {
          setError(result.error || 'Failed to verify certificate');
          return null;
        }

        return result.data || null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getCertificateById = useCallback(
    async (id: string): Promise<CertificateResponse | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await certificateApi.getCertificateById(id);

        if (!result.success) {
          setError(result.error || 'Failed to get certificate');
          return null;
        }

        return result.data || null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getCertificates = useCallback(async (): Promise<CertificateResponse[]> => {
    setLoading(true);
    setError(null);

    try {
      const result = await certificateApi.getCertificates();

      if (!result.success) {
        setError(result.error || 'Failed to load certificates');
        return [];
      }

      return result.data || [];
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadCertificatePdf = useCallback(
    async (id: string): Promise<boolean> => {
      setDownloading(true);
      setError(null);

      try {
        const result = await certificateApi.downloadCertificatePdf(id);

        if (!result.success) {
          setError(result.error || 'Failed to download certificate');
          return false;
        }

        return true;
      } finally {
        setDownloading(false);
      }
    },
    []
  );

  const downloadCertificate = useCallback(
    async (id: string, format: string = 'pdf'): Promise<boolean> => {
      setDownloading(true);
      setError(null);

      try {
        const result = await certificateApi.downloadCertificate(id, format);

        if (!result.success) {
          setError(result.error || 'Failed to download certificate');
          return false;
        }

        return true;
      } finally {
        setDownloading(false);
      }
    },
    []
  );

  return {
    loading,
    downloading,
    error,
    createCertificate,
    verifyCertificate,
    getCertificateById,
    getCertificates,
    downloadCertificatePdf,
    downloadCertificate,
    deleteCertificate,
  };
};
